import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/Detail/Card';
import Table from 'components/Table';
import TableChartRow from './TableChartRow';
import {
  DEFAULT_TABLE_CHART_CONFIG,
  getTableChartConfig,
  formatDate,
  getActualTimeRange,
  getChartRenderRequirements,
  chartDotMenu
} from './utils'
import Pagination from 'components/Pagination'
import { DEFAULT_PAGE_SIZE } from 'utils/constants';
import { orderBy, isEqual } from 'lodash'
import classNames from 'classnames';
import { ChartLoader } from './ChartLoader';
import { defaultNotifier } from 'functions/notificationHandler';
import { rawChartData, previewRawChartData } from '../../../../../api/dashboard'
import { CHART_DEFAULT_VALUES } from '../chartConfigurationsV2'
import { BLOCK, WARN } from './constants'

class TableChart extends Component {
  constructor(props) {
    super(props)
    this.state = {
      sort: {
        key: 'timeInterval',
        direction: 'asc',
      },
      isLoading: false,
      validData: false,
      dataErrorMessage: '',
      dataWarnMessage: '',
      chartData: {
        groupBy: [],
        data: []
      },
      metricColumns: [],
      tableChartConfig: { ...DEFAULT_TABLE_CHART_CONFIG },
      offset: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      isChartDataLoading: false,

      chartConfig: { ...CHART_DEFAULT_VALUES },
      hasMenu: false
    };
  }

  minMetrics = (data) => {
    // Accept either the full chartData object { data: [...] } or the array directly
    const arr = Array.isArray(data) ? data : data?.data;
    if (!Array.isArray(arr) || arr.length === 0) return {};

    // Minimum value aggregator over values
    return arr.reduce((acc, entry) => {
      if (!entry || typeof entry !== 'object' || !entry.values) return acc;
      for (let key in entry.values) {
        const value = parseFloat(entry.values[key]);
        if (Number.isNaN(value)) continue;
        acc[key] = acc[key] !== undefined ? Math.min(acc[key], value) : value;
      }
      return acc;
    }, {});
  };

  componentWillReceiveProps = (nextprops) => {
    if (!isEqual(this.props.isDashboardInitialized, nextprops.isDashboardInitialized)) {
      if (nextprops.isPreview) {
        return this.setPreviewData()
      }
      if (nextprops.isDashboardInitialized) {
        return this.setData()
      }
    }

    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  async componentDidMount() {
    const isOldChartConfig = typeof this.props.chart?.configuration === 'object' && this.props.chart?.configuration.hasOwnProperty('basicFields')
    this.setState({ hasMenu: !isOldChartConfig })

    if (!this.props.isPreview && !isOldChartConfig) {
      const chartConfig = JSON.parse(this.props.chart?.configuration)
      this.setState({ chartConfig: chartConfig }, () => { 
        this.setRawChartData()
      })
    }

    if (this.props.isPreview) {
      this.setState({ chartConfig: this.props.chartValues, hasMenu: false }, () => {
        this.setPreviewData()
      })
    }
    // else {
    //   this.setData()
    // }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.isPreview) {

      const currentChartValues = this.props.chartValues
      const prevChartValues = prevProps.chartValues

      // Chart values change - update config and potentially refresh
      if (!isEqual(currentChartValues, prevChartValues)) {
        this.setState({ chartConfig: { ...currentChartValues } }, () => {
          // Check if specific properties changed that require refresh
          if (!isEqual(currentChartValues.devices, prevChartValues.devices) ||
            !isEqual(currentChartValues.groupBy, prevChartValues.groupBy)) {
            return this.setPreviewData()
          }
        })
      }

      if (!isEqual(this.props.chartType, prevProps.chartType)) {
        return this.setPreviewData()
      }

      const currentDataStreams = this.props.dataStreams
      const prevDataStreams = prevProps.dataStreams

      if (!isEqual(currentDataStreams, prevDataStreams)) {
        return this.setPreviewData()
      }

      const currentChart = this.props.chart
      const prevChart = prevProps.chart
      const currentPreviewParams = this.props.previewParams
      const prevPreviewParams = prevProps.previewParams

      if (!isEqual(currentChart, prevChart) || !isEqual(currentPreviewParams, prevPreviewParams)) {
        return this.setPreviewData()
      }

      // Prevent infinite update loop: only handle refreshPreview on rising edge
      if (this.props.refreshPreview && !prevProps.refreshPreview) {
        return this.setState({
          tableChartConfig: this.props.chartValues
        })
      }
    }

    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    const prevIsDashboardInitialized = prevProps.isDashboardInitialized
    const actualIsDashboardInitialized = this.props.isDashboardInitialized
    if ((prevIsDashboardInitialized !== actualIsDashboardInitialized) && actualIsDashboardInitialized) {
      this.setData()
    }
  }

  setPreviewData = async () => {
    const { chart, previewParams, dashboardTimeRange, isPreview } = this.props

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setData = async () => {
    const { chart, dashboardTimeRange, dashboardDevices } = this.props
    this.setState({ isChartDataLoading: true })

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange)

    try {
      const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      if (hasBlockingIssues) {
        return this.setState({ isChartDataLoading: false })
      }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      this.setState({ isChartDataLoading: false })
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  checkChartRenderRequirements = (timeRange) => {
    const requirements = getChartRenderRequirements(this.state.chartConfig, timeRange)

    const warnMessages = requirements[WARN].map(r => r.message).join('\n')
    const blockMessages = requirements[BLOCK].map(r => r.message).join('\n')
    const hasWarningIssues = requirements[BLOCK].length > 0
    const hasBlockingIssues = requirements[BLOCK].length > 0

    if (hasWarningIssues) {
      this.setState({ dataWarnMessage: warnMessages })
    }

    if (hasBlockingIssues) {
      this.setState({
        validData: false,
        dataErrorMessage: blockMessages,
        chartData: { groupBy: [], data: [] }
      })
    }

    return {
      hasBlockingIssues
    }
  }

  totalMetrics = (data) => {
    // Accept either the full chartData object { data: [...] } or the array directly
    const arr = Array.isArray(data) ? data : data?.data;
    if (!Array.isArray(arr) || arr.length === 0) return {};

    // Maximum value aggregator over values
    return arr.reduce((acc, entry) => {
      if (!entry || typeof entry !== 'object' || !entry.values) return acc;
      for (let key in entry.values) {
        const value = parseFloat(entry.values[key]);
        if (Number.isNaN(value)) continue;
        acc[key] = acc[key] !== undefined ? Math.max(acc[key], value) : value;
      }
      return acc;
    }, {});
  };

  newTotalMetrics = (data) => {
    if (!Array.isArray(data) || data.length === 0) return {};
    return data.reduce((acc, entry) => {
      if (!entry || typeof entry !== 'object') return acc;
      for (let key in entry) {
        if (key === 'bucketed_time') continue;
        const value = parseFloat(entry[key]);
        if (Number.isNaN(value)) continue;
        acc[key] = acc[key] !== undefined ? Math.max(acc[key], value) : value;
      }
      return acc;
    }, {});
  };

  newMinMetrics = (data) => {
    if (!Array.isArray(data) || data.length === 0) return {};
    return data.reduce((acc, entry) => {
      if (!entry || typeof entry !== 'object') return acc;
      for (let key in entry) {
        if (key === 'bucketed_time') continue;
        const value = parseFloat(entry[key]);
        if (Number.isNaN(value)) continue;
        acc[key] = acc[key] !== undefined ? Math.min(acc[key], value) : value;
      }
      return acc;
    }, {});
  };

  onChangeLimit = (offset) => {
    this.setState({ offset: offset });
    this.setState({ currentPage: 1 });
  };

  setCurrentPage = (currentPage) => {
    this.setState({ currentPage: currentPage });
  };

  processColumns = () => {
    const { chartData, metricColumns, tableChartConfig, chartConfig } = this.state

    const columns = []
    const metrics = [...chartData.groupBy, ...metricColumns]
    const streams = chartConfig?.dataStreams?.streams || []

    // Determine table mode using new v2 flag when available; otherwise fall back
    // to the legacy tableChartConfig.isRawData. This ensures we only show the
    // "Time Interval" label for aggregated tables, while raw tables keep the
    // plain "time" label (or a custom raw interval title).
    const tableMode = chartConfig?.tableColumnMetricData
    const isRawTableMode = tableMode
      ? tableMode === 'raw'
      : !!tableChartConfig?.isRawData

    metrics.forEach((col) => {
      if (col === 'time') {
        if (!isRawTableMode) {
          // Aggregated table mode: show "Time Interval" (or configured title).
          // Use any legacy interval title first, then the v2 intervalTitle, and
          // finally fall back to the literal "Time Interval".
          const timeLabel =
            (tableChartConfig?.interval && tableChartConfig.interval.title) ||
            chartConfig.intervalTitle ||
            'Time Interval'

          columns.unshift({
            label: timeLabel,
            // Aggregated tables still render from the 'bucketed_time' field in
            // the row; keep the sortKey aligned with that underlying field so
            // TableChartRow's existing bucketed_time renderer can populate the
            // cells.
            sortKey: 'bucketed_time',
            isSortable: true,
            isLocked: true,
          })
        } else {
          // Raw table mode: keep the natural "time" label (or an explicit
          // intervalTitle when provided). Never coerce to "Time Interval".
          // Prefer an explicit stream label when the 'time' metric has one,
          // so the editor's label field can override the heading just like
          // other columns.
          const timeStream = streams.find(stream =>
            Array.isArray(stream?.metric?.metrics) && stream.metric.metrics.includes('time')
          )
          const timeStreamLabel = timeStream?.streamLabel?.value

          const rawTimeLabel =
            (timeStreamLabel && timeStreamLabel.trim().length > 0)
              ? timeStreamLabel.trim()
              : ((chartConfig.intervalTitle && chartConfig.intervalTitle.trim().length > 0)
                  ? chartConfig.intervalTitle.trim()
                  : 'time')

          columns.unshift({
            label: rawTimeLabel,
            sortKey: 'bucketed_time',
            isSortable: true,
            isLocked: true,
          })
        }
      } else {
        // Find the stream that contains this metric in its metric.metrics array
        const metricStream = streams.find(stream => 
          Array.isArray(stream?.metric?.metrics) && stream.metric.metrics.includes(col)
        )
        const streamLabel = metricStream?.streamLabel?.value
        const setColumnTitle = streamLabel || col
        columns.push({
          label: setColumnTitle,
          sortKey: col,
          isSortable: true,
          isLocked: true
        })
      }
    })

    return columns
  }

  onSorting = (sort) => {
    if (sort.key === 'timeInterval') {
      this.setState({
        sort,
        isSorted: true,
        chartData: {
          ...this.state.chartData,
          data:
            orderBy(
              this.state.chartData.data,
              [(item) => new Date(item.group.time)],
              [sort.direction]
            )
        }
      });
    }
    if (sort.key !== 'timeInterval') {
      this.setState({
        sort,
        isSorted: true,
        chartData: {
          ...this.state.chartData,
          data:
            orderBy(
              this.state.chartData.data,
              [(item) => {
                const raw = item?.values?.[sort.key] ?? item?.group?.[sort.key] ?? item?.[sort.key]
                const num = Number(raw)
                if (!Number.isNaN(num)) return num
                if (raw == null) return ''
                return typeof raw === 'string' ? raw.toLowerCase() : raw
              }],
              [sort.direction]
            )
        }
      });
    }
  };

  getDeviceList = () => {
    const { chartValues, devicesData } = this.props

    if (chartValues?.devices && !!chartValues.devices.length) {
      return chartValues.devices
    }

    return devicesData?.map(item => item.name)
  }

  setRawChartData = async () => {
    const { chart, dashboardTimeRange, isPreview, chartValues, isNewVersion } = this.props
    const { chartConfig } = this.state

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview, true, chartConfig)

    const groupByMetrics = chartConfig?.groupBy?.metrics || ['time']

    let data = {}

    // Ensure every configured stream has at least one metric selected
    const streams = chartConfig?.dataStreams?.streams || []
    const anyMissingMetrics = !streams.every(s => Array.isArray(s?.metric?.metrics) && s.metric.metrics.length > 0)
    // show loader immediately
    this.setState({ isChartDataLoading: true })

    if (anyMissingMetrics) {
      if (!this.props.isPreview) {
        // Suppress error during initial dashboard load
        return
      }
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: "Select a metric to display the preview",
        validData: false,
        isChartDataLoading: false
      })
    }

    if (this.props.sqlError) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: this.props.sqlError,
        validData: false
      })
    }

    if (this.props.isPreview && this.props.dataStreams) {
      const devices = this.getDeviceList();

      if (!devices.length) {
        return this.setState({ isChartDataLoading: false })
      }

      this.setState({ isChartDataLoading: true })

      const response = await previewRawChartData({
        dataStreams: this.props.dataStreams,
        visoDevices: devices,
        dashboardId: this.props.dashboardId
      })

      data = {
        errors: response?.data?.errors || null,
        data: response?.data?.data?.previewRawChartData
      }
      this.setState({ isChartDataLoading: false })
    } else {

      const devices = this.getDeviceList();

      if (!devices.length) {
        return this.setState({ isChartDataLoading: false })
      }

      if (devices.length) {
        this.setState({ isChartDataLoading: true })
        const response = await rawChartData({
          chartId: this.props.chart?.id,
          visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
          timeRange: { start: actualTimeRange.start, end: actualTimeRange.end }
        })

        data = {
          errors: response?.data?.errors || null,
          data: response?.data?.data?.rawChartData
        }

        this.setState({ isChartDataLoading: false })

        if (data.errors) {
          const errorMessage = data.errors?.[0]?.message
          return this.setState({
            dataErrorMessage: errorMessage,
            validData: false,
            isChartDataLoading: false
          })
        }
      }
    }
    
    if (data.data) {

      const merged = data?.data?.[0]?.result?.data?.map((_, i) => {
        const firstResult = data?.data?.[0]?.result
        const firstRow = firstResult?.data?.[i] || {}

        // Always expose a unified 'bucketed_time' field on the row so that
        // both raw and aggregated table modes can render the time column
        // consistently, regardless of whether the SQL returned 'bucketed_time'
        // or just 'time'.
        const timeValue =
          firstRow.bucketed_time !== undefined && firstRow.bucketed_time !== null
            ? firstRow.bucketed_time
            : firstRow.time

        const mergedEntry = {}
        if (timeValue !== undefined) {
          mergedEntry.bucketed_time = timeValue
        }

        // For each stream result, copy over its non-time field into the merged
        // row. Treat both 'bucketed_time' and 'time' as time fields to skip.
        data?.data?.forEach(d => {
          const fieldName = d.result.fields.find(f => f !== 'bucketed_time' && f !== 'time')
          if (!fieldName) return
          mergedEntry[fieldName] = d.result.data[i][fieldName]
        })

        return mergedEntry
      })

      const rawData = merged || [];
      const rawFields = [...new Set(data.data?.flatMap(item => item.result.fields))];

      // Normalize both 'bucketed_time' and 'time' to 'time' when comparing
      // against configured groupBy metrics.
      const normalizedFields = rawFields?.map(field =>
        (field === 'bucketed_time' || field === 'time') ? 'time' : field
      )

      const matchingRawFields = normalizedFields?.filter(field =>
        groupByMetrics.includes(field)
      )
      const nonMatchingRawFields = rawFields?.filter(field => {
        const normalized = (field === 'bucketed_time' || field === 'time') ? 'time' : field
        return !matchingRawFields.includes(normalized)
      })

      const xAxisGroupKeys = [...matchingRawFields]
      const yAxisGroupKeys = [...nonMatchingRawFields]

      const chartData = rawData?.map(row => {
        const normalizedRow = { ...row }

        // Preserve original value types for Y-axis fields.
        // Do NOT coerce to numbers here so that when decimals are set to 'From Data',
        // string values remain strings and render unchanged. Numeric formatting will
        // be handled later at render-time when a numeric decimal is explicitly chosen.
        // yAxisGroupKeys.forEach(key => {
        //   const val = normalizedRow[key];
        //   normalizedRow[key] = safeParseNumber(val)
        // })

        return normalizedRow
      })

      this.setState({
        chartData: {
          groupBy: xAxisGroupKeys,
          data: chartData,
          uniqueGroupValues: []
        },
        metricColumns: yAxisGroupKeys,
        dataErrorMessage: '',
        totalMetrics: this.newTotalMetrics(chartData),
        minMetrics: this.newMinMetrics(chartData),
        validData: true,
        // isChartDataLoading: false,
        // cartesianConfig: mapChartValuesToCartesianConfig(this.state.chartConfig)
      })//, () => { console.log('After changes: ', this.state.cartesianConfig) })
    }
  }

  // replaceStreamKeys = (data, dataStreams) => {
  //   if (!Array.isArray(data) || !dataStreams?.streams) return data;

  //   return data.map(item => {
  //     const newItem = { bucketed_time: item.bucketed_time };

  //     Object.keys(item).forEach((key, idx) => {
  //       if (key === "bucketed_time") return; // skip time field

  //       const stream = dataStreams.streams[idx-1]; // match by index
  //       const newKey = stream?.streamLabel?.value || key;

  //       debugger
  //       newItem[newKey] = item[key];
  //     });

  //     return newItem;
  //   });
  // }

  getErrorMessage = () => {
    if (!this.state.validData) {
      return this.state.dataErrorMessage
    }

    return ''
  }

  // Render error message
  renderErrorContext = () => {
    return (
      <div className='chart-error'>
        <div>
          {this.getErrorMessage()}
        </div>
        <div>
          {this.state.dataWarnMessage}
        </div>
      </div>
    )
  }

  hasError = () => {
    const {chartType, chartValues} = this.props;

    if (!this.state.validData) {
      return true
    }

    if(chartType === 'card' || chartType === 'table') {
      return false
    }

    return false
  }

  renderContent = () => {
    const { chart, height } = this.props;

    const { validData, dataErrorMessage, chartData, currentPage, color, dataWarnMessage } = this.state;

    // const payload = this.replaceStreamKeys(this.state.chartData?.data, this.state.chartConfig.dataStreams)

    return (
      <>
        {
          this.hasError()
            ?
            this.renderErrorContext()
            :
            <>
              {this.state.chartData?.data.length &&
                <div className='table-chart-wrapper'>
                  <div style={{ height: height - 63 }} className='table-chart-content'>
                    <div className='table-chart-scroll'>
                      <div className='width-100'>
                        <div className='table-chart'>
                          <Table
                            chartConfig={this.state.chartConfig}
                            chartValues={this.props.chartValues}
                            chart={chart}
                            rows={this.state.chartData?.data?.filter(
                              (item, index) => {
                                if (
                                  currentPage === 1
                                  && index < this.state.offset
                                ) {
                                  return item;
                                }

                                if (
                                  currentPage !== 1
                                  && index
                                  < (this.state.offset * currentPage)
                                  && index
                                  >= this.state.offset * (currentPage - 1)
                                ) {
                                  return item;
                                }

                                return false;
                              },
                            ) || []}
                            totalMetrics={this.state.totalMetrics}
                            minMetrics={this.state.minMetrics}
                            // columns={this.processColumns()?.main}
                            columns={this.processColumns()}
                            // notFoundIcon={<CarbonForIbmProduct size='24'/>}
                            // CheckboxImage={CarbonIBM}
                            RowComponent={TableChartRow}
                            inputType={this.props.inputType}
                            // isSelectable
                            colorOptions={this.state.colorOptions}
                            isLoading={this.props.isLoading}
                            detailRole={this.props.detailRole}
                            onSelectAll={this.props.onSelectAll}
                            // colorClassName={color}
                            hideSingleMenu={true}
                            hideCheckbox={true}
                            // withColumnSelect
                            customScrollbar={false}
                            application={this.props.application}
                            onSetNewVersion={this.props.onSetNewVersion}
                            onDeleteVersion={this.props.onDeleteVersion}
                            // selectedItems={this.props.selectedItems}
                            // onSelect={this.props.onSelect}
                            currentSortKey={this.state.sort && this.state.sort.key}
                            currentSortDir={this.state.sort && this.state.sort.direction}
                            onUpdateSort={this.onSorting}
                            rowClick={() => false}
                          />
                        </div>

                        {dataWarnMessage && <div className='chart-error'>
                          {dataWarnMessage}
                        </div>
                        }
                      </div>
                    </div>
                  </div>
                  <div className='table-chart-pagination'>
                    <Pagination
                      hideDivider
                      currentPage={this.state.currentPage}
                      setCurrentPage={this.setCurrentPage}
                      onChangeLimit={this.onChangeLimit}
                      limit={this.state.offset}
                      showMiniCount={true}
                      totalPage={chartData?.data?.length}
                      dropUp
                    />
                  </div>
                </div>
              }
            </>
        }
      </>
    )
  }

  render = () => {
    const { color, chart, isLegacy, isEditMode } = this.props;
    const isNewVersion = this.state.chartConfig.hasOwnProperty('version')
    const hasTitle = this.state.chartConfig?.chartTitle || chart.title
    const heightOffset = hasTitle ? 86 : 65

    return (
      <Card
        title={isNewVersion ? this.state.chartConfig?.chartTitle : chart.title}
        chartId={!this.state.isChartDataLoading && (chart?.id || '66dece43216604577e3ab1a0')}
        menu={this.state.hasMenu && !isLegacy ? 'dot' : ''}
        color={this.props.color}
        contentLoading={this.state.isChartDataLoading}
        className={classNames("overview chart-cards chart-cards--table", {
          "charts-card-preview": this.props.isPreview
        })}
        height={this.props.isPreview && this.props.height + heightOffset}
        titleClass="pd-b_15"
        content={this.state.isChartDataLoading ? <ChartLoader color={this.props.color} /> : this.renderContent()}
        onToogleMenu={this.props.onToogleMenu}
        dropDownMenu={this.state.hasMenu && !isLegacy && this.props.onEdit && chartDotMenu({
          color,
          showAI: this.props.showAI,
          onEdit: this.props.onEdit,
          onCopy: this.props.onCopy,
          onDelete: this.props.onDelete,
          onExplainKPI: () => this.props.onExplainKPI(chart?.id),
          onImproveChart: () => this.props.onImproveChart(chart?.id),
          isEditMode
        })}
      />
    )
  }
}

TableChart.propTypes = {
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  chart: PropTypes.object.isRequired,
  previewParams: PropTypes.any,
  dashboardDevices: PropTypes.any,
  dashboardTimeRange: PropTypes.any,
  height: PropTypes.any,
  isPreview: PropTypes.bool,
  refreshPreview: PropTypes.bool
};

export default TableChart;